import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Combobox } from '../../components/ui/combobox';
import { MultiSelectCombobox } from '../../components/ui/multi-select-combobox';
import { PageHeader } from '../../components/PageHeader';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { DimensionValueCombobox } from '../../components/DimensionValueCombobox';
import { Badge } from '../../components/ui/badge';
import { Loader2, Plus, Trash2, Upload, Download, Pencil, AlertTriangle } from 'lucide-react';
import { useDimensions, useMeasures } from '../../services/reportBuilder.service';
import { getGoalService, type Goal, type GoalDirection, type GoalImportResult } from '../../services/goal.service';
import { formatValue } from '../../services/formatting.service';
import { toastService } from '../../services/toast.service';
import { apiErrorMessage } from '../../services/api.service';

const goalService = getGoalService();

/** Goals per page. The list is server-paginated — see the note on `page` state. */
const PAGE_SIZE = 50;

/** Save a Blob to the user's disk. */
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface DraftGoal {
  id?: string;
  metric_id: string;
  value: string;
  direction: GoalDirection;
  notes: string;
  /** dimension_id -> value, as typed. The backend canonicalizes dates. */
  dimensionValues: Record<string, string>;
}

const EMPTY_DRAFT: DraftGoal = {
  metric_id: '',
  value: '',
  direction: 'ABOVE',
  notes: '',
  dimensionValues: {},
};

function useGoals(page: number, metricIds: string[]) {
  return useQuery({
    // metricIds is part of the key so switching the filter refetches rather than
    // re-rendering the previous metric's page.
    queryKey: ['goals', page, metricIds],
    queryFn: () => goalService.find({ page, pageSize: PAGE_SIZE, metricIds }),
    // Keeps the current page on screen while the next one loads instead of
    // flashing the loading state on every page change.
    placeholderData: (previous) => previous,
  });
}

/** Per-metric goal counts for the filter dropdown. Tenant-wide, not per page. */
function useGoalCountsByMetric() {
  return useQuery({
    queryKey: ['goalCountsByMetric'],
    queryFn: () => goalService.fetchCountsByMetric(),
  });
}

export default function Goals() {
  const queryClient = useQueryClient();
  // Server-paginated. It used to fetch pageSize 1000 and render whatever came
  // back, so a tenant past that had goals affecting every chart while being
  // invisible and uneditable here — and nothing said so.
  const [page, setPage] = useState(1);
  const [metricFilter, setMetricFilter] = useState<string[]>([]);
  const { data: goalsData, isLoading, error, isFetching } = useGoals(page, metricFilter);
  const { data: measures } = useMeasures();
  const { data: dimensions } = useDimensions();
  const { data: goalCountsByMetric } = useGoalCountsByMetric();

  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState<DraftGoal>(EMPTY_DRAFT);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [csvFileName, setCsvFileName] = useState('');
  const [preview, setPreview] = useState<GoalImportResult | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Goal | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const goals = goalsData?.records || [];
  const totalRecords = goalsData?.total_records ?? goals.length;
  const totalPages = Math.max(1, goalsData?.total_pages ?? 1);
  const firstOnPage = totalRecords === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastOnPage = (page - 1) * PAGE_SIZE + goals.length;

  const measureById = useMemo(
    () => new Map((measures || []).map((m) => [m.id, m])),
    [measures]
  );
  const dimensionById = useMemo(
    () => new Map((dimensions || []).map((d) => [d.id, d])),
    [dimensions]
  );

  const metricOptions = useMemo(
    () => (measures || []).map((m) => ({ value: m.id, label: m.name })),
    [measures]
  );

  // Same options with a goal count on each, for the filter dropdown. The editor's
  // metric picker deliberately doesn't get these — how many goals a metric
  // already has says nothing about whether to set another one on it.
  //
  // Metrics with no goals are left out entirely: picking one could only ever
  // narrow the list to nothing. A metric that's currently selected stays in the
  // list even at zero — deleting a filtered metric's last goal drops its count,
  // and dropping the option with it would leave the trigger showing a raw id
  // with no way to switch the filter off from the dropdown.
  const metricFilterOptions = useMemo(
    () =>
      metricOptions
        .filter(
          (option) =>
            (goalCountsByMetric?.[option.value] ?? 0) > 0 ||
            metricFilter.includes(option.value)
        )
        .map((option) => ({
          ...option,
          badge: (
            <span className="text-xs tabular-nums text-muted-foreground">
              {goalCountsByMetric?.[option.value] ?? 0}
            </span>
          ),
        })),
    [metricOptions, goalCountsByMetric, metricFilter]
  );
  const dimensionOptions = useMemo(
    () => (dimensions || []).map((d) => ({ value: d.id, label: d.name })),
    [dimensions]
  );

  const handleMetricFilterChange = useCallback((ids: string[]) => {
    setMetricFilter(ids);
    // The current page is almost certainly past the end of the narrowed list,
    // which would show an empty page with matches sitting on page 1.
    setPage(1);
  }, []);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['goals'] });
    // Creating, deleting, or re-pointing a goal changes the per-metric counts
    // the filter dropdown shows.
    queryClient.invalidateQueries({ queryKey: ['goalCountsByMetric'] });
    // The chart payload embeds goal values and plan rows, so every cached
    // analysis result is stale. Key must match useAnalysisData's 'analysisData'.
    queryClient.invalidateQueries({ queryKey: ['analysisData'] });
  }, [queryClient]);

  const saveMutation = useMutation({
    mutationFn: async (d: DraftGoal) => {
      const payload = {
        metric_id: d.metric_id,
        value: d.value,
        direction: d.direction,
        notes: d.notes,
        dimension_values: Object.entries(d.dimensionValues)
          .filter(([, v]) => v.trim() !== '')
          .map(([dimension_id, value]) => ({ dimension_id, value })),
      };
      return d.id
        ? goalService.update(d.id, payload)
        : goalService.create(payload);
    },
    onSuccess: () => {
      invalidate();
      setEditorOpen(false);
      setDraft(EMPTY_DRAFT);
      toastService.success('Goal saved');
    },
    onError: (e: unknown) => {
      toastService.error(apiErrorMessage(e, 'Could not save goal'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => goalService.delete(id),
    onSuccess: () => {
      invalidate();
      setDeleteTarget(null);
      // Deleting the last row of the last page would otherwise leave the user on
      // an empty page with no way back.
      if (goals.length === 1 && page > 1) {
        setPage((p) => p - 1);
      }
      toastService.success('Goal deleted');
    },
    onError: (e: unknown) =>
      toastService.error(apiErrorMessage(e, 'Could not delete goal')),
  });

  const importMutation = useMutation({
    mutationFn: () => goalService.importCsv(csvText, false),
    onSuccess: (result) => {
      invalidate();
      setUploadOpen(false);
      setCsvText('');
      setCsvFileName('');
      setPreview(null);
      toastService.success(
        `Imported ${result.created} new and ${result.updated} updated goal(s)`
      );
    },
    onError: (e: unknown) => toastService.error(apiErrorMessage(e, 'Import failed')),
  });

  const handleFilePicked = useCallback(async (file: File) => {
    setCsvFileName(file.name);
    setPreview(null);
    setPreviewError(null);

    let text: string;
    try {
      text = await file.text();
    } catch {
      setCsvText('');
      setPreviewError('Could not read that file.');
      return;
    }
    setCsvText(text);

    // Dry run first — a bad header rejects the whole file, and bad rows are
    // reported individually, so the user should see it before anything is saved.
    try {
      setPreview(await goalService.importCsv(text, true));
    } catch (e: unknown) {
      // The backend's message is the whole value of a dry run: which column it
      // didn't recognize, or that the file is too large.
      setPreviewError(apiErrorMessage(e, 'Could not read that CSV'));
    }
  }, []);

  const downloadTemplate = useCallback(async () => {
    try {
      saveBlob(await goalService.fetchTemplate(), 'goals_template.csv');
    } catch (e: unknown) {
      toastService.error(apiErrorMessage(e, 'Could not download the template'));
    }
  }, []);

  const openEditor = useCallback((goal?: Goal) => {
    if (goal) {
      setDraft({
        id: goal.id,
        metric_id: goal.metric_id,
        value: String(goal.value),
        direction: goal.direction,
        notes: goal.notes || '',
        dimensionValues: Object.fromEntries(
          goal.dimension_values.map((dv) => [dv.dimension_id, dv.value])
        ),
      });
    } else {
      setDraft(EMPTY_DRAFT);
    }
    setEditorOpen(true);
  }, []);

  const draftDimensionRows = useMemo(
    () => Object.entries(draft.dimensionValues),
    [draft.dimensionValues]
  );

  const canSaveDraft =
    draft.metric_id !== '' &&
    draft.value.trim() !== '' &&
    draftDimensionRows.some(([, v]) => v.trim() !== '');

  const renderSlice = (goal: Goal) => (
    <div className="flex flex-wrap gap-1">
      {goal.dimension_values.map((dv) => (
        <Badge key={dv.dimension_id} variant="secondary" className="font-normal">
          {dimensionById.get(dv.dimension_id)?.name ?? 'Unknown dimension'}: {dv.value}
        </Badge>
      ))}
    </div>
  );

  return (
    <div className="w-full flex flex-col bg-page-bg overflow-hidden" style={{ height: 'calc(100vh)' }}>
      <PageHeader
        title="Goals"
        actions={
          <>
            <MultiSelectCombobox
              options={metricFilterOptions}
              value={metricFilter}
              onValueChange={handleMetricFilterChange}
              placeholder="All metrics"
              emptyText="No metrics have goals yet."
              className="w-64"
            />
            <Button variant="outline" onClick={() => setUploadOpen(true)} className="flex items-center gap-2">
              <Upload className="h-4 w-4" />
              Upload CSV
            </Button>
            <Button onClick={() => openEditor()} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              New Goal
            </Button>
          </>
        }
      />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        <div className="mb-6 w-full rounded-lg border border-border bg-muted/40 p-4">
          <p className="text-sm text-foreground">
            A goal is the number you're aiming for on one metric, for one specific
            part of your data — say{' '}
            <span className="font-medium">
              Revenue, May 2026, Mobile channel: at or above $120,000
            </span>
            . Once it's set, Pliable shows it next to your actuals so you can see
            where you stand.
          </p>
          <dl className="mt-3 space-y-2 text-sm text-muted-foreground">
            <div>
              <dt className="inline font-medium text-foreground">
                Where you'll see it:
              </dt>{' '}
              <dd className="inline">
                On table charts, beside the real number, with how far over or under
                you are. Use <span className="font-medium">Show Goals</span> in the
                chart editor to hide them on a particular chart.
              </dd>
            </div>
            <div>
              <dt className="inline font-medium text-foreground">
                Group your table the same way:
              </dt>{' '}
              <dd className="inline">
                A goal only lands on rows broken out the way you set it. A goal for
                May 2026 shows on a table grouped by month — not by day or quarter —
                and if you also set it on Mobile, the table needs a channel
                breakdown too.
              </dd>
            </div>
            <div>
              <dt className="inline font-medium text-foreground">
                Above or below:
              </dt>{' '}
              <dd className="inline">
                Pick which side of the number counts as winning.{' '}
                <span className="font-medium">At or above</span> for things you want
                more of, like revenue; <span className="font-medium">at or below</span>{' '}
                for things you want less of, like cost per order.
              </dd>
            </div>
            <div>
              <dt className="inline font-medium text-foreground">
                You can plan ahead:
              </dt>{' '}
              <dd className="inline">
                Set a target for a month you have no data for yet and it still shows
                up, marked as plan, so you can watch the actuals fill in against it.
                Setting a lot of them at once is what{' '}
                <span className="font-medium">Upload CSV</span> is for.
              </dd>
            </div>
          </dl>
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading goals…
          </div>
        )}

        {error && (
          <div className="p-4 bg-destructive/10 border border-destructive/30 rounded text-sm">
            Could not load goals: {apiErrorMessage(error, 'unknown error')}
          </div>
        )}

        {!isLoading && !error && goals.length === 0 && metricFilter.length > 0 && (
          <div className="p-6 bg-muted/50 border border-border rounded text-sm text-muted-foreground flex items-center justify-between gap-4">
            <span>
              No goals for {metricFilter.length === 1 ? 'that metric' : 'those metrics'}.
            </span>
            <Button variant="outline" size="sm" onClick={() => handleMetricFilterChange([])}>
              Clear filter
            </Button>
          </div>
        )}

        {!isLoading && !error && goals.length === 0 && metricFilter.length === 0 && (
          <div className="p-6 bg-muted/50 border border-border rounded text-sm text-muted-foreground">
            No goals yet. Add one by hand, or upload a CSV with a{' '}
            <code className="text-xs">metric</code> column, a{' '}
            <code className="text-xs">value</code> column, and one column per
            dimension (e.g. <code className="text-xs">Month</code>,{' '}
            <code className="text-xs">Channel</code>).
          </div>
        )}

        {goals.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Metric</TableHead>
                <TableHead>Applies to</TableHead>
                <TableHead className="text-right">Goal</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {goals.map((goal) => {
                const measure = measureById.get(goal.metric_id);
                return (
                  <TableRow key={goal.id}>
                    <TableCell className="font-medium">
                      {measure?.name ?? (
                        <span className="text-muted-foreground italic">
                          Deleted metric
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{renderSlice(goal)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatValue({
                        value: goal.value,
                        format: measure?.formatter || 'NUMERIC',
                      })}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {goal.direction === 'ABOVE' ? 'At or above' : 'At or below'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[240px] truncate">
                      {goal.notes}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditor(goal)}
                          aria-label="Edit goal"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(goal)}
                          disabled={deleteMutation.isPending}
                          aria-label="Delete goal"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {goals.length > 0 && (
          <div className="flex items-center justify-between pt-2">
            <div className="text-sm text-muted-foreground">
              Showing {firstOnPage.toLocaleString()}–{lastOnPage.toLocaleString()} of{' '}
              {totalRecords.toLocaleString()} goal{totalRecords === 1 ? '' : 's'}
              {metricFilter.length > 0 && (
                <>
                  {' '}for {metricFilter.length}{' '}
                  {metricFilter.length === 1 ? 'metric' : 'metrics'}
                </>
              )}
              {isFetching && <Loader2 className="inline h-3 w-3 animate-spin ml-2" />}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || isFetching}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground tabular-nums">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || isFetching}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ---------- Delete confirmation ---------- */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this goal?</AlertDialogTitle>
            <AlertDialogDescription>
              The{' '}
              {deleteTarget ? measureById.get(deleteTarget.metric_id)?.name || 'metric' : 'metric'}{' '}
              goal for{' '}
              {deleteTarget?.dimension_values?.length
                ? deleteTarget.dimension_values
                    .map((dv) => dv.value)
                    .join(' · ')
                : 'this slice'}{' '}
              will be removed, and any chart showing it will stop comparing
              against plan. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget?.id) deleteMutation.mutate(deleteTarget.id);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ---------- Add / edit ---------- */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{draft.id ? 'Edit Goal' : 'New Goal'}</DialogTitle>
            <DialogDescription>
              Pick the metric and the exact slice this target applies to. For a
              monthly goal, add your calendar's Month dimension with a date like
              2026-05-01.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Metric</label>
              <Combobox
                options={metricOptions}
                value={draft.metric_id}
                onValueChange={(v) => setDraft((d) => ({ ...d, metric_id: v }))}
                placeholder="Select a metric…"
              />
            </div>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Applies to</CardTitle>
                <CardDescription className="text-xs">
                  The exact slice this target matches. Values come from the data —
                  type to search, or enter one that doesn't exist yet, like a
                  future month.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {draftDimensionRows.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Add at least one dimension so the goal has something to match.
                  </p>
                )}
                {draftDimensionRows.map(([dimensionId, value]) => (
                  <div key={dimensionId} className="flex items-center gap-2">
                    <div
                      className="w-[40%] shrink-0 text-sm truncate"
                      title={dimensionById.get(dimensionId)?.name ?? dimensionId}
                    >
                      {dimensionById.get(dimensionId)?.name ?? dimensionId}
                    </div>
                    <div className="flex-1 min-w-0">
                      <DimensionValueCombobox
                        dimensionId={dimensionId}
                        value={value}
                        onValueChange={(v) =>
                          setDraft((d) => ({
                            ...d,
                            dimensionValues: { ...d.dimensionValues, [dimensionId]: v },
                          }))
                        }
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Remove dimension"
                      onClick={() =>
                        setDraft((d) => {
                          const next = { ...d.dimensionValues };
                          delete next[dimensionId];
                          return { ...d, dimensionValues: next };
                        })
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Combobox
                  options={dimensionOptions.filter(
                    (o) => !(o.value in draft.dimensionValues)
                  )}
                  value=""
                  onValueChange={(v) =>
                    v &&
                    setDraft((d) => ({
                      ...d,
                      dimensionValues: { ...d.dimensionValues, [v]: '' },
                    }))
                  }
                  placeholder="Add a dimension…"
                />
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <div className="flex-1 space-y-1.5">
                <label className="text-sm font-medium">Goal value</label>
                <Input
                  value={draft.value}
                  placeholder="120000"
                  onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))}
                />
              </div>
              <div className="flex-1 space-y-1.5">
                <label className="text-sm font-medium">Good is</label>
                <Combobox
                  options={[
                    { value: 'ABOVE', label: 'At or above goal' },
                    { value: 'BELOW', label: 'At or below goal' },
                  ]}
                  value={draft.direction}
                  onValueChange={(v) =>
                    setDraft((d) => ({ ...d, direction: v as GoalDirection }))
                  }
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Notes</label>
              <Input
                value={draft.notes}
                placeholder="Optional"
                onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => saveMutation.mutate(draft)}
              disabled={!canSaveDraft || saveMutation.isPending}
            >
              {saveMutation.isPending && (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              )}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- CSV upload ---------- */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Goals CSV</DialogTitle>
            <DialogDescription>
              Needs a <code>metric</code> column, a <code>value</code> column, and
              one column per dimension. Optional: <code>direction</code> (ABOVE or
              BELOW) and <code>notes</code>. Re-uploading replaces goals on the same
              metric and slice rather than duplicating them.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFilePicked(file);
                  // Reset so re-picking the same path fires onChange again. The
                  // whole point of the error preview is "fix the file and retry",
                  // and without this the retry silently did nothing.
                  e.target.value = '';
                }}
              />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                Choose file…
              </Button>
              <span className="text-sm text-muted-foreground">
                {csvFileName || 'No file selected'}
              </span>
              {/* A button, not an <a href>: the endpoint is behind
                  @with_auth_context() and an anchor navigation sends no
                  Authorization header, so a link 401s. */}
              <button
                type="button"
                onClick={downloadTemplate}
                className="ml-auto text-sm inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
              >
                <Download className="h-3.5 w-3.5" />
                Template
              </button>
            </div>

            {previewError && (
              <div className="p-3 bg-destructive/10 border border-destructive/30 rounded text-sm flex gap-2">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{previewError}</span>
              </div>
            )}

            {preview && (
              <div className="space-y-2">
                <div className="text-sm">
                  <strong>{preview.created}</strong> new,{' '}
                  <strong>{preview.updated}</strong> to update,{' '}
                  {/* error_count is the real total; `errors` is capped by the API
                      so a wholly broken file can't return one object per row. */}
                  <strong>{preview.error_count ?? preview.errors.length}</strong> row
                  {(preview.error_count ?? preview.errors.length) === 1 ? '' : 's'} with problems.
                </div>
                {preview.errors.length > 0 && (
                  <div className="max-h-48 overflow-auto border border-border rounded">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-20">Row</TableHead>
                          <TableHead>Problem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.errors.map((err, i) => (
                          <TableRow key={`${err.row}-${i}`}>
                            <TableCell className="tabular-nums">{err.row}</TableCell>
                            <TableCell className="text-sm">{err.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {!!preview.errors_truncated && (
                      <p className="px-3 py-2 text-xs text-muted-foreground">
                        …and {preview.errors_truncated} more.
                      </p>
                    )}
                  </div>
                )}
                {preview.errors.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Importing will skip these rows and save the rest.
                  </p>
                )}
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => importMutation.mutate()}
              disabled={
                !csvText ||
                !!previewError ||
                !preview ||
                preview.created + preview.updated === 0 ||
                importMutation.isPending
              }
            >
              {importMutation.isPending && (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              )}
              Import {preview ? preview.created + preview.updated : 0} goal(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
